import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, BellRing, X, Check, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global Tidsblock reminder system.
 *
 * Every 30s while mounted:
 *  1. Loads today's schedule_template + completions
 *  2. Finds any block where time matches HH:MM "now" (±2 min) and isn't done/skipped/dismissed
 *  3. Shows an in-app banner (top of screen) + plays a kawaii chime
 *  4. Fires a Browser Notification if permission granted
 *
 * Dismissal is per-block-per-day, stored in localStorage so reload won't re-fire.
 * "Bocka av" calls /api/schedule-template/toggle to mark done in DB.
 */
export default function TidsblockReminder() {
  const [active, setActive] = useState([]); // [{time, label}]
  const lastFiredRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const tplRef = useRef({ blocks: {}, completions: {} });

  const todayKey = () => new Date().toLocaleDateString('sv-SE');
  const dismissKey = (time) => `tidsblock_dismiss_${todayKey()}_${time}`;

  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      // 3-note kawaii ascending chime: C5 - E5 - G5
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.18, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.4);
      });
    } catch { /* silent */ }
  }, []);

  const fetchTemplate = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/schedule-template`, { withCredentials: true });
      tplRef.current = {
        blocks: data?.blocks || {},
        completions: data?.completions || {},
      };
    } catch { /* silent — user may be logged out */ }
  }, []);

  const tick = useCallback(() => {
    const { blocks, completions } = tplRef.current;
    if (!blocks || Object.keys(blocks).length === 0) return;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    const due = [];
    Object.keys(blocks).forEach((time) => {
      const label = (blocks[time] || '').trim();
      if (!label) return;
      const [h, m] = time.split(':').map(Number);
      const blockMin = h * 60 + m;
      // Fire within +0..+2 minutes window (so a 60s poll catches every slot)
      const diff = nowMin - blockMin;
      if (diff < 0 || diff > 2) return;
      const comp = completions[time] || {};
      if (comp.done || comp.skipped) return;
      if (localStorage.getItem(dismissKey(time))) return;
      if (lastFiredRef.current.has(time)) return;
      due.push({ time, label });
    });

    if (due.length === 0) return;

    due.forEach((d) => lastFiredRef.current.add(d.time));
    setActive((prev) => [...prev, ...due]);
    playChime();

    // Browser notification (when tab is in background)
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
      due.forEach((d) => {
        try {
          new Notification(`🔔 ${d.time} — Tidsblock`, {
            body: d.label,
            tag: `tidsblock-${d.time}`,
            icon: '/favicon.ico',
          });
        } catch { /* silent */ }
      });
    }
  }, [playChime]);

  // Polling
  useEffect(() => {
    fetchTemplate();
    const a = setInterval(fetchTemplate, 120 * 1000); // re-sync template every 2 min
    const b = setInterval(tick, 30 * 1000); // poll every 30s
    tick();
    return () => { clearInterval(a); clearInterval(b); };
  }, [fetchTemplate, tick]);

  // Reset daily at midnight (also clears the "lastFiredRef" so today's blocks re-arm)
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 30);
    const msToMidnight = tomorrow - now;
    const t = setTimeout(() => {
      lastFiredRef.current.clear();
      setActive([]);
    }, msToMidnight);
    return () => clearTimeout(t);
  }, []);

  // Ask for Notification permission once on first activity (when user has actual blocks)
  useEffect(() => {
    if (active.length === 0) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      try { Notification.requestPermission().catch(() => {}); } catch { /* silent */ }
    }
  }, [active.length]);

  const dismiss = (time) => {
    localStorage.setItem(dismissKey(time), '1');
    setActive((prev) => prev.filter((b) => b.time !== time));
  };

  const markDone = async (time) => {
    try {
      await axios.post(`${API}/schedule-template/toggle`, {
        time, date: todayKey(), done: true,
      }, { withCredentials: true });
      // Update local cache so next tick won't re-fire
      tplRef.current.completions = {
        ...(tplRef.current.completions || {}),
        [time]: { ...(tplRef.current.completions?.[time] || {}), done: true },
      };
    } catch { /* silent */ }
    dismiss(time);
  };

  if (active.length === 0) return null;

  return (
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[90] flex flex-col gap-2 max-w-md w-[calc(100%-1rem)]"
      data-testid="tidsblock-reminders"
    >
      {active.map((b) => (
        <div
          key={b.time}
          className="neo-card p-3 bg-gradient-to-r from-[#FFC8DD] via-[#FFE8F5] to-[#A2D2FF] border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-[pulse_2s_ease-in-out_2]"
          data-testid={`tidsblock-reminder-${b.time.replace(':', '')}`}
        >
          <div className="flex items-start gap-2">
            <div className="w-10 h-10 rounded-full bg-white border-2 border-black flex items-center justify-center shrink-0">
              <BellRing className="w-5 h-5 text-[#D06892] animate-bounce" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wider">
                <Clock className="w-3 h-3" /> {b.time}
              </div>
              <p className="font-['Fredoka'] font-bold text-base leading-tight break-words">{b.label}</p>
            </div>
            <button
              onClick={() => dismiss(b.time)}
              className="shrink-0 w-7 h-7 rounded-full bg-white border-2 border-black flex items-center justify-center"
              data-testid={`tidsblock-dismiss-${b.time.replace(':', '')}`}
              title="Stäng"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => markDone(b.time)}
              className="flex-1 neo-btn px-3 py-2 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1"
              data-testid={`tidsblock-done-${b.time.replace(':', '')}`}
            >
              <Check className="w-4 h-4" /> Klart!
            </button>
            <button
              onClick={() => dismiss(b.time)}
              className="neo-btn px-3 py-2 bg-white text-xs"
              data-testid={`tidsblock-snooze-${b.time.replace(':', '')}`}
            >
              <Bell className="w-3 h-3 inline" /> Senare
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
